import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { searchCities } from "../../api/cityService";
import { addAddress, updateAddress } from "../../api/addressService";
import "./clientTheme.css";
import "./AddressFormModal.css";

function getErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (data?.message) return data.message;
  if (data?.title) return data.title;
  return fallback;
}

export default function AddressFormModal({ address, onClose, onSaved }) {
  const { t } = useTranslation(["addressFormModal", "common"]);
  const isEditing = Boolean(address);

  const [title, setTitle] = useState(address?.title || "");
  const [cityQuery, setCityQuery] = useState(address?.cityNameEnglish || address?.city || "");
  const [selectedCity, setSelectedCity] = useState(
    address
      ? { nameHebrew: address.city, nameEnglish: address.cityNameEnglish, nameArabic: address.cityNameArabic }
      : null,
  );
  const [cityOptions, setCityOptions] = useState([]);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const [street, setStreet] = useState(address?.street || "");
  const [buildingNumber, setBuildingNumber] = useState(address?.buildingNumber || "");
  const [apartmentNumber, setApartmentNumber] = useState(address?.apartmentNumber || "");
  const [additionalDetails, setAdditionalDetails] = useState(address?.additionalDetails || "");
  const [isDefault, setIsDefault] = useState(address?.isDefault || false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const cityFieldRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (cityFieldRef.current && !cityFieldRef.current.contains(e.target)) {
        setCityDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleCityInput(value) {
    setCityQuery(value);
    setSelectedCity(null);
    setCityDropdownOpen(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setCityLoading(true);
      try {
        const results = await searchCities(value.trim(), 20);
        setCityOptions(results);
      } catch {
        setCityOptions([]);
      } finally {
        setCityLoading(false);
      }
    }, 300);
  }

  function handleCityFocus() {
    setCityDropdownOpen(true);
    if (cityOptions.length === 0 && !cityLoading) {
      handleCityInput(cityQuery);
    }
  }

  function selectCity(city) {
    setSelectedCity(city);
    setCityQuery(city.nameEnglish || city.nameHebrew);
    setCityDropdownOpen(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selectedCity) {
      setError(t("chooseCityError"));
      return;
    }
    if (!street.trim()) {
      setError(t("enterStreetError"));
      return;
    }

    setSaving(true);
    setError("");
    const payload = {
      title: title.trim() || null,
      city: selectedCity.nameHebrew,
      street: street.trim(),
      buildingNumber: buildingNumber.trim() || null,
      apartmentNumber: apartmentNumber.trim() || null,
      additionalDetails: additionalDetails.trim() || null,
      isDefault,
    };

    try {
      const saved = isEditing ? await updateAddress(address.addressId, payload) : await addAddress(payload);
      onSaved(saved);
    } catch (err) {
      setError(getErrorMessage(err, t("saveErrorFallback")));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="client-login-overlay" onMouseDown={onClose}>
      <div className="address-form-modal" onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="client-login-close" aria-label={t("common:close")} onClick={onClose}>
          &times;
        </button>

        <h2 className="client-login-title">{isEditing ? t("editAddressTitle") : t("addAddressTitle")}</h2>

        <form onSubmit={handleSubmit} className="address-form">
          <label className="address-form-field">
            <span className="address-form-label">{t("labelFieldLabel")}</span>
            <input
              type="text"
              className="address-form-input"
              placeholder={t("labelPlaceholder")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={50}
            />
          </label>

          <div className="address-form-field" ref={cityFieldRef}>
            <span className="address-form-label">{t("cityFieldLabel")}</span>
            <input
              type="text"
              className="address-form-input"
              placeholder={t("cityPlaceholder")}
              value={cityQuery}
              onChange={(e) => handleCityInput(e.target.value)}
              onFocus={handleCityFocus}
              autoComplete="off"
            />
            {cityDropdownOpen && (
              <div className="address-form-city-dropdown">
                {cityLoading ? (
                  <div className="address-form-city-status">{t("citySearching")}</div>
                ) : cityOptions.length === 0 ? (
                  <div className="address-form-city-status">{t("noCitiesFound")}</div>
                ) : (
                  cityOptions.map((city) => (
                    <button
                      type="button"
                      key={city.cityId}
                      className="address-form-city-option"
                      onClick={() => selectCity(city)}
                    >
                      {city.nameEnglish || city.nameHebrew}
                      {city.districtName ? <span className="address-form-city-district">{city.districtName}</span> : null}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <label className="address-form-field">
            <span className="address-form-label">{t("streetFieldLabel")}</span>
            <input
              type="text"
              className="address-form-input"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              maxLength={200}
            />
          </label>

          <div className="address-form-row">
            <label className="address-form-field">
              <span className="address-form-label">{t("buildingFieldLabel")}</span>
              <input
                type="text"
                className="address-form-input"
                value={buildingNumber}
                onChange={(e) => setBuildingNumber(e.target.value)}
                maxLength={20}
              />
            </label>
            <label className="address-form-field">
              <span className="address-form-label">{t("apartmentFieldLabel")}</span>
              <input
                type="text"
                className="address-form-input"
                value={apartmentNumber}
                onChange={(e) => setApartmentNumber(e.target.value)}
                maxLength={20}
              />
            </label>
          </div>

          <label className="address-form-field">
            <span className="address-form-label">{t("additionalDetailsFieldLabel")}</span>
            <input
              type="text"
              className="address-form-input"
              placeholder={t("additionalDetailsPlaceholder")}
              value={additionalDetails}
              onChange={(e) => setAdditionalDetails(e.target.value)}
              maxLength={200}
            />
          </label>

          <label className="address-form-checkbox-row">
            <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
            <span>{t("setAsDefaultLabel")}</span>
          </label>

          {error && <p className="client-login-error">{error}</p>}

          <button type="submit" className="client-login-button" disabled={saving}>
            {saving ? t("savingButton") : isEditing ? t("saveChangesButton") : t("addAddressButton")}
          </button>
        </form>
      </div>
    </div>
  );
}
